import React, { useRef, useEffect } from 'react'
import gsap from 'gsap'
import PropTypes from 'prop-types'
import { isMobile } from 'react-device-detect'

// Hooks
import { useScrolledAnimation } from '../useScrolledAnimation'

// Styles
import { AnimateLetterboxWrapper, AnimateLetterboxInner } from './index.style'

const AnimateLetterbox = ({
  id,
  duration,
  delay,
  opacity,
  scaledX,
  scaledY,
  position,
  pointerEvents,
  syncWithScroll,
  useInView,
  isInView,
  debug,
  children,
}) => {
  const wrapperRef = useRef(null)
  const innerRef = useRef(null)
  const maskIn = useRef(null)

  useScrolledAnimation({
    id: id,
    transition: maskIn,
    sync: syncWithScroll,
    delay: delay,
    useInView: useInView,
    isInView: isInView,
    debug: debug,
  })

  useEffect(() => {
    if (!wrapperRef.current || !innerRef.current) return

    maskIn.current = gsap.timeline({
      paused: true,
      defaults: {
        duration: duration,
        ease: syncWithScroll ? 'power1.out' : 'power2.inOut',
      },
    })

    !isMobile &&
      maskIn.current.fromTo(
        wrapperRef.current,
        {
          scaleX: scaledX,
          scaleY: scaledY,
        },
        {
          scaleX: 1.0001,
          scaleY: 1.0001,
        },
        0
      )

    maskIn.current.fromTo(
      innerRef.current,
      {
        scaleX: (1 / scaledX) * 1.2,
        scaleY: (1 / scaledY) * 1.2,
      },
      {
        y: 0,
        scaleX: 0.9999,
        scaleY: 0.9999,
      },
      0
    )
  }, [
    wrapperRef,
    innerRef,
    opacity,
    scaledX,
    scaledY,
    duration,
    syncWithScroll,
  ])

  return (
    <AnimateLetterboxWrapper
      ref={wrapperRef}
      pointer={pointerEvents}
      data-scroll
      data-scroll-id={id}
    >
      <AnimateLetterboxInner ref={innerRef} position={position}>
        {children}
      </AnimateLetterboxInner>
    </AnimateLetterboxWrapper>
  )
}

AnimateLetterbox.propTypes = {
  id: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  delay: PropTypes.number,
  duration: PropTypes.number,
  opacity: PropTypes.number,
  scaledX: PropTypes.number,
  scaledY: PropTypes.number,
  position: PropTypes.oneOf(['absolute', 'relative']),
  pointerEvents: PropTypes.oneOf(['none', '']),
  useInView: PropTypes.bool,
  isInView: PropTypes.bool,
  syncWithScroll: PropTypes.bool,
}

AnimateLetterbox.defaultProps = {
  delay: 0,
  duration: 0.4,
  opacity: 0.01,
  scaledX: 0.9,
  scaledY: 0.9,
  position: 'absolute',
  pointerEvents: 'none',
  useInView: true,
  isInView: false,
  syncWithScroll: true,
}

export default AnimateLetterbox
